// https://github.com/IdentityModel/oidc-client-js/wiki
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { LogLevel, HubConnectionBuilder } from '@microsoft/signalr';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import { ApplicationPaths, ApplicationName, QueryParameterNames } from './ApiAuthorizationConstants';

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail',
};

const createArguments = (state) => ({ useReplaceToNavigate: true, data: state });

const error = (message) => ({ status: AuthenticationResultStatus.Fail, message });

const success = (state) => ({ status: AuthenticationResultStatus.Success, state });

const redirect = () => ({ status: AuthenticationResultStatus.Redirect });

const buildLoginUrl = () => {
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    const isAuthPath = currentPath.startsWith(`${ApplicationPaths.ApiAuthorizationPrefix}/`);
    if (isAuthPath || currentPath === '/') {
        return ApplicationPaths.IdentityLoginPath;
    }
    return `${ApplicationPaths.IdentityLoginPath}?${QueryParameterNames.ReturnUrl}=${encodeURIComponent(currentPath)}`;
};

// Fallback returnUrl stored in sessionStorage before signinRedirect, used to recover the
// deep link if signinCallback later fails (state already consumed, cross-origin storage,
// storage cleared). The OIDC `state` parameter is the primary carrier; this is a backup.
const PRE_AUTH_RETURN_URL_KEY = `${ApplicationName}.preAuthReturnUrl`;

export class AuthorizeService {
    _callbacks = [];

    _nextSubscriptionId = 0;

    _user = null;

    _isAuthenticated = false;

    _hubConnected = null;

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    _userValidated = false;

    _validating = false;

    _renewingAccessToken = null;

    async isAuthenticated() {
        const user = await this.getUser();

        return !!user;
    }

    validateState = () => {
        if (this._validating || this._userValidated) {
            return;
        }

        this._validating = true;

        this.userManager.querySessionStatus()
            .then(() => {
                this._userValidated = true;
                this._validating = false;
            })
            .catch((e) => {
                console.log('Error logging in', e);

                this.userManager.removeUser();
                window.location.href = buildLoginUrl();
            });
    };

    debouncedValidateState = debounce(this.validateState, 1000);

    async getUser() {
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();

        if (user?.expired) {
            await this.userManager.removeUser();

            return null;
        }

        if (!this._userValidated && !this._validating) {
            this.debouncedValidateState();
        }

        return user && user.profile;
    }

    async getAccessToken() {
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.access_token;
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(state) {
        await this.ensureUserManagerInitialized();
        try {
            const silentUser = await this.userManager.signinSilent(createArguments());
            this.updateState(silentUser);
            return success(state);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            console.log('Silent authentication error: ', silentError);

            // Kinda hacky, but some Safari users experience issues where on first load they are unable to fetch the openID discovery endpoint
            // Redirecting to the correct login path "fixes" this. Return immediately so we
            // don't race with the popup/redirect fallthrough below — both setting
            // window.location.href in the same tick caused inconsistent destinations.
            if (silentError?.message === 'Load failed') {
                window.location.href = buildLoginUrl();
                return redirect();
            }

            try {
                if (this._popUpDisabled) {
                    throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.');
                }

                const popUpUser = await this.userManager.signinPopup(createArguments());
                this.updateState(popUpUser);
                return success(state);
            } catch (popUpError) {
                if (popUpError.message === 'Popup window closed') {
                    // The user explicitly cancelled the login action by closing an opened popup.
                    return error('The user closed the window.');
                } if (!this._popUpDisabled) {
                    console.log('Popup authentication error: ', popUpError);
                }

                // PopUps might be blocked by the user, fallback to redirect
                try {
                    if (state?.returnUrl) {
                        sessionStorage.setItem(PRE_AUTH_RETURN_URL_KEY, state.returnUrl);
                    }
                    await this.userManager.signinRedirect(createArguments(state));
                    return redirect();
                } catch (redirectError) {
                    sessionStorage.removeItem(PRE_AUTH_RETURN_URL_KEY);
                    console.log('Redirect authentication error: ', redirectError);
                    return error(redirectError);
                }
            }
        }
    }

    async completeSignIn(url) {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.signinCallback(url);
            // Prefer the OIDC `state` carrier (signed/validated by oidc-client-ts).
            // Fall back to the sessionStorage backup written before signinRedirect
            // when state.returnUrl is missing — observed in Chrome where the OIDC
            // state object can survive the redirect but lose its embedded `data`
            // payload, leaving the SPA without a deep-link target.
            const fallback = sessionStorage.getItem(PRE_AUTH_RETURN_URL_KEY);
            sessionStorage.removeItem(PRE_AUTH_RETURN_URL_KEY);
            this.updateState(user);
            let resolvedState = user && user.state;
            if (!resolvedState?.returnUrl && fallback) {
                resolvedState = { ...(resolvedState || {}), returnUrl: fallback };
            }
            return success(resolvedState);
        } catch (e) {
            console.log('There was an error signing in: ', e);
            // Recover the deep link from the pre-auth fallback rather than dropping to origin
            // root or to the bare Razor login (buildLoginUrl collapses /authentication/* to
            // /Identity/Account/Login with no returnUrl). Restart through the SPA login route
            // so a clean OIDC flow runs with the original destination preserved.
            const fallback = sessionStorage.getItem(PRE_AUTH_RETURN_URL_KEY);
            sessionStorage.removeItem(PRE_AUTH_RETURN_URL_KEY);
            if (fallback) {
                window.location.replace(`${ApplicationPaths.Login}?${QueryParameterNames.ReturnUrl}=${encodeURIComponent(fallback)}`);
            } else {
                window.location.replace(buildLoginUrl());
            }
            return redirect();
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state) {
        await this.ensureUserManagerInitialized();
        try {
            if (this._popUpDisabled) {
                throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.');
            }

            await this.userManager.signoutPopup(createArguments());
            this.updateState(undefined);

            return success(state);
        } catch (popupSignOutError) {
            console.log('Popup signout error: ', popupSignOutError);
            try {
                await this.userManager.signoutRedirect(createArguments(state));
                return redirect();
            } catch (redirectSignOutError) {
                console.log('Redirect signout error: ', redirectSignOutError);
                return error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url) {
        await this.ensureUserManagerInitialized();

        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);

            return success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return error(error);
        }
    }

    async refreshToken() {
        await httpClient.put(ApiRoutes.auth.updateToken());
        await this.renewAccessToken();
    }

    // Obtains a fresh access token via a silent sign-in against the still-valid login
    // session (e.g. after the API rejects the cached token). Concurrent callers share a
    // single in-flight renewal. Resolves to the new token, or null when the renewal
    // fails so callers can fall back to their normal unauthorized handling.
    renewAccessToken() {
        if (!this._renewingAccessToken) {
            this._renewingAccessToken = this.ensureUserManagerInitialized()
                .then(() => this.userManager.signinSilent())
                .then(() => this.userManager.getUser())
                .then((user) => {
                    this.updateState(user);
                    return user?.access_token ?? null;
                })
                .catch((error) => {
                    console.error('Error renewing access token:', error);
                    return null;
                })
                .finally(() => {
                    this._renewingAccessToken = null;
                });
        }

        return this._renewingAccessToken;
    }

    updateState(user) {
        this._user = user;
        this._isAuthenticated = !!this._user;

        if (user) {
            this.connectToPermissionsHub();
        } else {
            this.disconnectPermissionsHub();
        }

        this.notifySubscribers();
    }

    subscribe(callback) {
        this._nextSubscriptionId += 1;
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId });

        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) => (element.subscription === subscriptionId ? { found: true, index } : { found: false }))
            .filter((element) => element.found === true);

        if (subscriptionIndex.length !== 1) {
            console.error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        } else {
            this._callbacks.splice(subscriptionIndex[0].index, 1);
        }
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i += 1) {
            const { callback } = this._callbacks[i];
            callback();
        }
    }

    async ensureUserManagerInitialized() {
        if (this.userManager !== undefined) {
            return;
        }

        const response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
        if (!response.ok) {
            throw new Error(`Could not load settings for '${ApplicationName}'`);
        }

        const settings = await response.json();
        settings.automaticSilentRenew = true;
        settings.includeIdTokenInSilentRenew = true;
        settings.loadUserInfo = true;
        settings.userStore = new WebStorageStateStore({
            prefix: ApplicationName,
            store: window.localStorage,
        });

        const currentTenantId = window.sessionStorage.getItem('currentTenantId');

        if (currentTenantId) {
            settings.extraHeaders = {
                'X-CurrentTenantId': currentTenantId,
            };
        }

        const impersonatingUserId = window.sessionStorage.getItem('impersonating');

        if (impersonatingUserId) {
            settings.extraHeaders = {
                'X-Impersonating': impersonatingUserId,
            };
        }

        this.userManager = new UserManager(settings);

        this.userManager.events.addUserSignedOut(async () => {
            await this.userManager.removeUser();
            this.updateState(undefined);
        });

        this.connectToPermissionsHub();
    }

    disconnectPermissionsHub = () => {
        if (this._hubConnected) {
            this._hubConnected.stop();
            this._hubConnected.off();

            this._hubConnected = null;
        }
    };

    connectToPermissionsHub = async () => {
        const user = await this.getUser();

        if (!user || this._hubConnected) {
            return;
        }

        this._hubConnected = new HubConnectionBuilder()
            .withUrl('permissionsHub')
            .configureLogging(LogLevel.Error)
            .withAutomaticReconnect()
            .build();

        this._hubConnected.on('PermissionsChanged', async (userId) => {
            try {
                if (user && user.sub === userId) {
                    await this.refreshToken();
                    return;
                } else {
                    console.log('No user found. User might be logged out.');
                }
            } catch (error) {
                console.error('Error in PermissionsChanged handler:', error);
            }
        });

        this._hubConnected
            .start()
            .then(() => {
                this._hubConnected.invoke('JoinGroup', `Permission-${user.sub}`);
            })
            .catch((e) => console.log('Error connecting to Permissions Hub: ', e));
    };

    static get instance() {
        return authService; // eslint-disable-line
    }
}

const authService = new AuthorizeService();

export default authService;